import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaCalculator,
  FaChartBar,
  FaClipboardList,
  FaGraduationCap,
  FaHandsHelping,
  FaLightbulb,
  FaUniversity,
} from 'react-icons/fa';
import { getOfferBandColor } from '../../utils/jupasSurveyResultsDisplay';

function KpiCard({ icon: Icon, label, value, hint, accent = 'primary' }) {
  const accents = {
    primary: 'from-primary/10 to-primary/5 text-primary border-primary/15',
    emerald: 'from-emerald-500/10 to-emerald-500/5 text-emerald-700 border-emerald-500/15 dark:text-emerald-300',
    sky: 'from-sky-500/10 to-sky-500/5 text-sky-700 border-sky-500/15 dark:text-sky-300',
    amber: 'from-amber-500/10 to-amber-500/5 text-amber-700 border-amber-500/15 dark:text-amber-300',
  };

  return (
    <div className={`rounded-2xl border bg-gradient-to-br p-4 shadow-sm ${accents[accent] || accents.primary}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</p>
          <p className="mt-1 text-3xl font-bold tabular-nums text-ink">{value}</p>
          {hint ? <p className="mt-1 text-[11px] leading-relaxed text-ink-muted">{hint}</p> : null}
        </div>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface/70 shadow-sm">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      </div>
    </div>
  );
}

function HorizontalBarChart({ rows, maxCount, renderLabel, barColor = 'bg-primary/70' }) {
  if (!rows?.length) return null;
  const peak = maxCount || Math.max(...rows.map((row) => row.count), 1);

  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const width = Math.max(4, Math.round((row.count / peak) * 100));
        return (
          <li key={row.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <div className="min-w-0">
              <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                <span className="truncate font-medium text-ink">{renderLabel ? renderLabel(row.key) : row.key}</span>
                <span className="shrink-0 tabular-nums text-ink-muted">{row.count}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-surface-muted">
                <div
                  className={`h-full rounded-full transition-all ${barColor}`}
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function BandBarChart({ rows, t }) {
  if (!rows?.length) {
    return <p className="text-sm text-ink-muted">{t('jupasSurveyResults.analyticsNoData')}</p>;
  }

  const peak = Math.max(...rows.map((row) => row.count), 1);

  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const width = Math.max(4, Math.round((row.count / peak) * 100));
        const bandKey = String(row.key).charAt(0).toUpperCase();
        return (
          <li key={row.key}>
            <div className="mb-1 flex items-center justify-between gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 font-medium text-ink">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: getOfferBandColor(bandKey) }}
                  aria-hidden
                />
                {row.key}
              </span>
              <span className="tabular-nums text-ink-muted">{row.count}</span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-surface-muted">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${width}%`,
                  backgroundColor: getOfferBandColor(bandKey),
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RatingHistogram({ rows, avg, avgLabel, t }) {
  const scale = [1, 2, 3, 4, 5];
  const countByKey = Object.fromEntries((rows || []).map((row) => [String(row.key), row.count]));
  const peak = Math.max(...scale.map((score) => countByKey[String(score)] || 0), 1);

  return (
    <div>
      <div className="mb-3 flex items-end justify-between gap-3">
        <p className="text-xs font-medium text-ink-muted">{avgLabel}</p>
        <p className="text-2xl font-bold tabular-nums text-ink">
          {avg == null ? '—' : Number(avg).toFixed(1)}
          <span className="ml-1 text-sm font-medium text-ink-subtle">/5</span>
        </p>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {scale.map((score) => {
          const count = countByKey[String(score)] || 0;
          const height = Math.max(8, Math.round((count / peak) * 100));
          return (
            <div key={score} className="flex flex-col items-center gap-1">
              <div className="flex h-16 w-full items-end justify-center rounded-lg bg-surface-muted/80 px-1">
                <div
                  className="w-full max-w-8 rounded-t-md bg-primary/75 transition-all"
                  style={{ height: `${height}%` }}
                  title={t('jupasSurveyResults.analyticsRatingCount', { score, count })}
                />
              </div>
              <span className="text-[10px] font-bold tabular-nums text-ink-muted">{score}</span>
              <span className="text-[9px] tabular-nums text-ink-subtle">{count || ''}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UsageSplitBar({ yes, no, yesLabel, noLabel }) {
  const total = (yes || 0) + (no || 0);
  const yesPct = total ? Math.round(((yes || 0) / total) * 100) : 0;
  const noPct = total ? 100 - yesPct : 0;

  return (
    <div>
      <div className="flex h-3 overflow-hidden rounded-full bg-surface-muted">
        {yesPct > 0 ? (
          <div className="h-full bg-primary/80" style={{ width: `${yesPct}%` }} />
        ) : null}
        {noPct > 0 ? (
          <div className="h-full bg-edge-strong/40" style={{ width: `${noPct}%` }} />
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
        <span>{yesLabel}: <strong className="text-ink">{yes || 0}</strong> ({yesPct}%)</span>
        <span>{noLabel}: <strong className="text-ink">{no || 0}</strong> ({noPct}%)</span>
      </div>
    </div>
  );
}

function AnalyticsPanel({ title, icon: Icon, children, className = '' }) {
  return (
    <section className={`overflow-hidden rounded-2xl border border-edge/80 bg-surface/95 shadow-sm backdrop-blur-sm ${className}`}>
      <header className="flex items-center gap-2 border-b border-edge bg-surface-muted/40 px-4 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-3.5 w-3.5" aria-hidden />
        </span>
        <h3 className="text-sm font-bold text-ink">{title}</h3>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function JupasSurveyAnalyticsSummary({ analytics, loading = false }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="mt-6 flex items-center justify-center rounded-2xl border border-edge/80 bg-surface/90 px-6 py-12 shadow-sm">
        <p className="text-sm text-ink-muted">{t('jupasSurveyResults.analyticsLoading')}</p>
      </div>
    );
  }

  if (!analytics || analytics.total === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-edge bg-surface/70 px-6 py-10 text-center shadow-sm">
        <FaClipboardList className="mx-auto h-8 w-8 text-ink-subtle" aria-hidden />
        <p className="mt-3 text-sm text-ink-muted">{t('jupasSurveyResults.analyticsEmpty')}</p>
      </div>
    );
  }

  const loggedInCount = Math.max(0, analytics.total - (analytics.anonymousCount || 0));
  const productUsers = (analytics.usedCalculatorYes || 0) + (analytics.usedInterestYes || 0);
  const productUserPct = analytics.total
    ? Math.round((productUsers / analytics.total) * 100)
    : 0;
  const recentMomentum = analytics.last7Days > 0
    ? Math.round((analytics.last7Days / Math.max(analytics.last30Days, 1)) * 100)
    : 0;

  const coreScoreSubjects = [
    ['english', t('jupasSurvey.fields.english')],
    ['math', t('jupasSurvey.fields.math')],
  ];

  return (
    <div className="mt-6 space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={FaClipboardList}
          label={t('jupasSurveyResults.analyticsTotal')}
          value={analytics.total}
          hint={t('jupasSurveyResults.analyticsAccountSplit', {
            loggedIn: loggedInCount,
            anonymous: analytics.anonymousCount || 0,
          })}
          accent="primary"
        />
        <KpiCard
          icon={FaChartBar}
          label={t('jupasSurveyResults.analyticsRecentActivity')}
          value={analytics.last7Days}
          hint={t('jupasSurveyResults.analyticsRecentHint', {
            last30: analytics.last30Days,
            momentum: recentMomentum,
          })}
          accent="sky"
        />
        <KpiCard
          icon={FaHandsHelping}
          label={t('jupasSurveyResults.analyticsVolunteers')}
          value={analytics.volunteerCount}
          hint={t('jupasSurveyResults.analyticsVolunteerHint')}
          accent="emerald"
        />
        <KpiCard
          icon={FaLightbulb}
          label={t('jupasSurveyResults.analyticsProductReach')}
          value={`${productUserPct}%`}
          hint={t('jupasSurveyResults.analyticsProductReachHint', { count: productUsers })}
          accent="amber"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalyticsPanel title={t('jupasSurveyResults.analyticsOffers')} icon={FaUniversity}>
          <div className="space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                {t('jupasSurveyResults.analyticsByInstitution')}
              </p>
              {analytics.byInstitution?.length ? (
                <HorizontalBarChart
                  rows={analytics.byInstitution}
                  renderLabel={(key) => t(`jupasSurvey.institutions.${key}`, { defaultValue: key })}
                  barColor="bg-sky-500/70"
                />
              ) : (
                <p className="text-sm text-ink-muted">{t('jupasSurveyResults.analyticsNoData')}</p>
              )}
            </div>

            <div className="border-t border-edge pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                {t('jupasSurveyResults.analyticsByBand')}
              </p>
              <BandBarChart rows={analytics.byOfferBand} t={t} />
            </div>
          </div>
        </AnalyticsPanel>

        <AnalyticsPanel title={t('jupasSurveyResults.analyticsTopProgrammes')} icon={FaGraduationCap}>
          {analytics.topJupasCodes?.length ? (
            <HorizontalBarChart
              rows={analytics.topJupasCodes.slice(0, 8)}
              barColor="bg-emerald-500/70"
            />
          ) : (
            <p className="text-sm text-ink-muted">{t('jupasSurveyResults.analyticsNoData')}</p>
          )}
          {(analytics.spnYes > 0 || analytics.spnNo > 0) ? (
            <p className="mt-4 rounded-xl border border-edge/70 bg-surface-muted/40 px-3 py-2 text-xs text-ink-muted">
              {t('jupasSurveyResults.analyticsSpnHint', {
                yes: analytics.spnYes || 0,
                no: analytics.spnNo || 0,
              })}
            </p>
          ) : null}
        </AnalyticsPanel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalyticsPanel title={t('jupasSurveyResults.analyticsEdviseCalculator')} icon={FaCalculator}>
          <div className="space-y-4">
            <UsageSplitBar
              yes={analytics.usedCalculatorYes}
              no={analytics.usedCalculatorNo}
              yesLabel={t('jupasListSubmission.comment.yes')}
              noLabel={t('jupasListSubmission.comment.no')}
            />
            <RatingHistogram
              rows={analytics.calculatorHelpfulnessDist}
              avg={analytics.avgCalculatorHelpfulness}
              avgLabel={t('jupasSurveyResults.analyticsCalculatorAvg')}
              t={t}
            />
          </div>
        </AnalyticsPanel>

        <AnalyticsPanel title={t('jupasSurveyResults.analyticsEdviseInterest')} icon={FaLightbulb}>
          <div className="space-y-4">
            <UsageSplitBar
              yes={analytics.usedInterestYes}
              no={analytics.usedInterestNo}
              yesLabel={t('jupasListSubmission.comment.yes')}
              noLabel={t('jupasListSubmission.comment.no')}
            />
            <RatingHistogram
              rows={analytics.interestHelpfulnessDist}
              avg={analytics.avgInterestHelpfulness}
              avgLabel={t('jupasSurveyResults.analyticsInterestHelpAvg')}
              t={t}
            />
            <div className="border-t border-edge pt-3">
              <RatingHistogram
                rows={analytics.interestAccuracyDist}
                avg={analytics.avgInterestAccuracy}
                avgLabel={t('jupasSurveyResults.analyticsInterestAccAvg')}
                t={t}
              />
            </div>
            {analytics.interestLowHelpReasonDist?.length ? (
              <div className="border-t border-edge pt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                  {t('jupasSurveyResults.analyticsInterestLowHelpReasons')}
                </p>
                <HorizontalBarChart
                  rows={analytics.interestLowHelpReasonDist}
                  renderLabel={(key) => t(`jupasListSubmission.comment.interestLowHelpReasons.${key}`, { defaultValue: key })}
                  barColor="bg-amber-500/70"
                />
              </div>
            ) : null}
          </div>
        </AnalyticsPanel>
      </div>

      <AnalyticsPanel title={t('jupasSurveyResults.analyticsScoreSnapshot')} icon={FaChartBar}>
        <p className="mb-4 text-xs text-ink-muted">{t('jupasSurveyResults.analyticsScoreSnapshotHint')}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {coreScoreSubjects.map(([key, label]) => (
            <div key={key} className="rounded-xl border border-edge/70 bg-surface-muted/30 p-3">
              <p className="mb-2 text-xs font-semibold text-ink">{label}</p>
              {analytics.coreScores?.[key]?.length ? (
                <HorizontalBarChart
                  rows={analytics.coreScores[key].slice(0, 6)}
                  barColor="bg-primary/60"
                />
              ) : (
                <p className="text-sm text-ink-muted">{t('jupasSurveyResults.analyticsNoData')}</p>
              )}
            </div>
          ))}
        </div>
      </AnalyticsPanel>
    </div>
  );
}
